// Renders the compact active-action widget and coordinates its expanded, DAP, and transfer workflows.
import React from 'react';
import {
  Card,
  CardContent,
  Button,
  CircularProgress,
  FormControl,
  makeStyles,
  Select,
  Typography,
} from '@rentacenter/racstrap';

import { ContainerContext } from '../../../app/App';
import { ReactComponent as ExpandIcon } from '../../../assets/images/fullscreen-mode.svg';
import { DashboardContext } from '../../../context/DashboardContext';
import {
  CompactStoreActionCategory,
  StoreAction,
  StoreActionPage,
} from './storeActionDomain';
import {
  useStoreActionDapActivation,
  useStoreActionRole,
} from './storeActionDap';
import {
  filterStoreActionItemsByCompactCategory,
  formatStoreActionCount,
  getCompactCategoryCounts,
} from './storeActionFilters';
import { getStoreActionMessage } from './storeActionLocalization';
import {
  composeExpandedPages,
  useStoreActionActiveListQuery,
  useStoreActionPriceTagQueueQuery,
} from './storeActionQueries';
import { composePriceTagQueueAction } from './storeActionPriceTag';
import { StoreActionExpandedDialog } from './StoreActionExpandedDialog';
import { StoreActionPagination } from './StoreActionPagination';
import { StoreActionRow } from './StoreActionRow';
import {
  StoreActionTransferActivation,
  StoreActionTransferDialog,
} from './StoreActionTransferDialog';
import { hasInventoryId } from './StoreActionTransferDialog';
import { useStoreActionCategoryFilter } from './useStoreActionCategoryFilter';

const SPACE_BETWEEN = 'space-between';
const ACTION_BLUE = '#2179FE';
const INLINE_FLEX = 'inline-flex';

const useStyles = makeStyles({
  card: {
    backgroundColor: '#fff',
    border: 0,
    borderRadius: 22,
    boxShadow: '0 3px 6px rgba(14, 58, 147, 0.102)',
    display: 'flex',
    flexDirection: 'column',
    height: 271,
    minHeight: 0,
    width: '100%',
  },
  content: {
    boxSizing: 'border-box',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    padding: 14,
    '@media (max-width: 720px)': {
      padding: 14,
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: 11,
    justifyContent: SPACE_BETWEEN,
    marginBottom: 0,
    '@media (max-width: 600px)': {
      alignItems: 'flex-start',
      flexWrap: 'wrap',
    },
  },
  headerControls: {
    alignItems: 'center',
    display: 'flex',
    gap: 7,
    '@media (max-width: 600px)': {
      flex: '1 0 100%',
      justifyContent: SPACE_BETWEEN,
    },
  },
  heading: {
    color: '#000',
    fontSize: 16,
    fontWeight: 700,
    lineHeight: '23px',
  },
  filter: {
    minWidth: 135,
    '& select': {
      fontSize: 12,
      paddingBottom: 5,
      paddingTop: 5,
    },
  },
  list: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: 2,
    marginTop: 14,
  },
  loading: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    minHeight: 120,
  },
  loadMore: {
    display: 'block',
    margin: '11px auto 0',
  },
  grid: {
    alignItems: 'start',
    display: 'grid',
    gap: 11,
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    '@media (max-width: 960px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    '@media (max-width: 600px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  row: {
    backgroundColor: '#fff',
    border: '1px solid #d5dce6',
    borderRadius: 7,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    padding: '11px 16px',
    '@media (max-width: 720px)': {
      padding: '14px',
    },
    '& .store-action-row__content': {
      cursor: 'pointer',
      minWidth: 0,
    },
    '& .store-action-row__header': {
      alignItems: 'flex-start',
      display: 'flex',
      gap: 7,
      justifyContent: SPACE_BETWEEN,
    },
    '& .store-action-row__title': {
      flex: 1,
      minWidth: 0,
      color: '#000',
      fontWeight: 600,
      lineHeight: 1.35,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& .store-action-row__chip': {
      border: '1px solid currentColor',
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 700,
      lineHeight: 1.2,
      padding: '3px 5px',
      whiteSpace: 'nowrap',
    },
    '& .store-action-row__date, & .store-action-row__date *': {
      color: '#576C7B',
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '18px',
    },
    '& .store-action-row__action': {
      alignItems: 'center',
      background: 'transparent',
      border: 0,
      color: ACTION_BLUE,
      cursor: 'pointer',
      display: INLINE_FLEX,
      justifyContent: 'center',
      minHeight: 29,
      minWidth: 29,
      padding: 4,
    },
    '& .store-action-row__action:disabled': {
      color: '#94a3b8',
      cursor: 'default',
    },
    '& .store-action-row__footer': {
      alignItems: 'flex-end',
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
    },
    '& .store-action-row__status': {
      color: '#64748b',
      fontSize: 11,
    },
    '& .store-action-row__virtual-action': {
      alignItems: 'center',
      backgroundColor: ACTION_BLUE,
      border: 0,
      borderRadius: '50%',
      color: '#1769aa',
      cursor: 'pointer',
      display: INLINE_FLEX,
      font: 'inherit',
      height: 29,
      justifyContent: 'center',
      padding: 0,
      width: 29,
      '& svg': {
        color: '#fff',
        fontSize: 14,
      },
    },
  },
  expand: {
    alignItems: 'center',
    background: 'transparent',
    border: 0,
    color: ACTION_BLUE,
    display: INLINE_FLEX,
    justifyContent: 'center',
    minHeight: 29,
    minWidth: 29,
    padding: 4,
    '& svg': {
      height: 21,
      maxHeight: 21,
      maxWidth: 21,
      width: 21,
    },
    '&:hover': {
      backgroundColor: '#eef4fb',
    },
  },
});

type StoreActionActiveListProps = {
  readonly storeNumber: string;
};

type StoreActionHost = {
  readonly GetEmployeeId: () => string;
  readonly GetRole: () => string;
};

const getHostRole = (host: StoreActionHost | undefined): string =>
  host ? host.GetRole().trim() : '';

const getCoworkerId = (
  host: StoreActionHost | undefined,
  hostRole: string
): string => {
  if (hostRole) {
    return '';
  }

  return host ? host.GetEmployeeId() : '';
};

const CATEGORY_MESSAGE_KEYS: Readonly<
  Record<
    CompactStoreActionCategory,
    Parameters<typeof getStoreActionMessage>[0]
  >
> = {
  ALL: 'categoryAll',
  AGR: 'categoryAgreement',
  INV: 'categoryInventory',
  CUS: 'categoryCustomer',
  FACTPAY: 'categoryInvoice',
  OTHER: 'categoryOther',
};

const useStoreActionTransferActivation = (): {
  readonly activateTransfer: (
    item: StoreAction,
    type: 'EAITF' | 'EAITT',
    trigger: HTMLElement
  ) => void;
  readonly closeTransfer: () => void;
  readonly transferActivation: StoreActionTransferActivation | null;
} => {
  const [transferActivation, setTransferActivation] =
    React.useState<StoreActionTransferActivation | null>(null);
  const activateTransfer = (
    item: StoreAction,
    type: 'EAITF' | 'EAITT',
    trigger: HTMLElement
  ): void => {
    if (hasInventoryId(item)) {
      setTransferActivation({ item, trigger, type });
    }
  };
  const closeTransfer = (): void => setTransferActivation(null);

  return { activateTransfer, closeTransfer, transferActivation };
};

const composeCompactPage = (
  queryData: ReturnType<typeof useStoreActionActiveListQuery>['data'],
  priceTagData: ReturnType<typeof useStoreActionPriceTagQueueQuery>['data']
): StoreActionPage | null => {
  const composition = composeExpandedPages(queryData ? queryData.pages : []);

  if (composition.pages.length === 0 || priceTagData === undefined) {
    return null;
  }

  return composePriceTagQueueAction(
    {
      ...composition.pages[0],
      items: composition.items,
      total: composition.total,
    },
    priceTagData
  );
};

const getCompactView = (
  queryData: ReturnType<typeof useStoreActionActiveListQuery>['data'],
  priceTagData: ReturnType<typeof useStoreActionPriceTagQueueQuery>['data']
) => {
  const page = composeCompactPage(queryData, priceTagData);

  return {
    counts: page
      ? getCompactCategoryCounts(page.countsByCategory, page.total)
      : null,
    page,
  };
};

type StoreActionCompactResultsProps = {
  readonly role: string;
  readonly classes: ReturnType<typeof useStyles>;
  readonly onDssActivate: (item: StoreAction, storeNumber: string) => void;
  readonly onTransferActivate: (
    item: StoreAction,
    type: 'EAITF' | 'EAITT',
    trigger: HTMLElement
  ) => void;
  readonly page: StoreActionPage | null;
  readonly priceTagQuery: ReturnType<typeof useStoreActionPriceTagQueueQuery>;
  readonly query: ReturnType<typeof useStoreActionActiveListQuery>;
  readonly storeNumber: string;
  readonly visibleItems: ReadonlyArray<StoreAction>;
};

const StoreActionCompactResults: React.FC<StoreActionCompactResultsProps> = ({
  role,
  classes,
  onDssActivate,
  onTransferActivate,
  page,
  priceTagQuery,
  query,
  storeNumber,
  visibleItems,
}) => (
  <>
    {query.isLoading || priceTagQuery.isLoading ? (
      <div
        aria-busy="true"
        aria-label={getStoreActionMessage('loading')}
        className={classes.loading}
        role="status"
      >
        <CircularProgress />
      </div>
    ) : null}
    {page && page.items.length === 0 ? (
      <p>{getStoreActionMessage('pendingEmpty')}</p>
    ) : null}
    {page && page.items.length > 0 && visibleItems.length === 0 ? (
      <p>{getStoreActionMessage('criteriaEmpty')}</p>
    ) : null}
    {visibleItems.length > 0 ? (
      <div
        aria-label={getStoreActionMessage('activeList')}
        aria-busy={
          query.isFetching ||
          query.isFetchingNextPage ||
          priceTagQuery.isFetching
        }
        className={classes.list}
        role="list"
      >
        <div className={classes.grid}>
          {visibleItems.map((item) => (
            <StoreActionRow
              className={classes.row}
              item={item}
              key={String(item.storeActionItemId)}
              onDssActivate={onDssActivate}
              onTransferActivate={onTransferActivate}
              role={role}
              storeNumber={storeNumber}
            />
          ))}
        </div>
        <StoreActionPagination
          className={classes.loadMore}
          hasNextPage={Boolean(query.hasNextPage && page)}
          isFetchingNextPage={query.isFetchingNextPage}
          isRefetching={query.isRefetching}
          onLoadMore={() => query.fetchNextPage({ cancelRefetch: false })}
        />
      </div>
    ) : null}
  </>
);

export const StoreActionActiveList: React.FC<StoreActionActiveListProps> = ({
  storeNumber,
}) => {
  const classes = useStyles();
  const container = React.useContext(ContainerContext) as
    | StoreActionHost
    | undefined;
  const dashboard = React.useContext(DashboardContext) as {
    readonly setActionEvent: (appointment: unknown) => void;
  };
  const hostRole = getHostRole(container);
  const coworkerId = getCoworkerId(container, hostRole);
  const roleQuery = useStoreActionRole(hostRole, coworkerId);
  const role = hostRole || roleQuery.data || '';
  const isRoleResolved = Boolean(
    hostRole || roleQuery.isSuccess || !coworkerId
  );
  const onDssActivate = useStoreActionDapActivation(dashboard.setActionEvent);
  const query = useStoreActionActiveListQuery(
    storeNumber,
    isRoleResolved,
    role
  );
  const priceTagQuery = useStoreActionPriceTagQueueQuery(storeNumber);
  const [category, setCategory] =
    React.useState<CompactStoreActionCategory>('ALL');
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { activateTransfer, closeTransfer, transferActivation } =
    useStoreActionTransferActivation();
  const { counts, page } = getCompactView(query.data, priceTagQuery.data);
  const { categories, selectedCategory } = useStoreActionCategoryFilter(
    category,
    setCategory,
    counts
  );
  const visibleItems = page
    ? filterStoreActionItemsByCompactCategory(page.items, selectedCategory)
    : [];

  return (
    <Card
      className={classes.card}
      component="section"
      aria-labelledby="store-action-heading"
    >
      <CardContent className={classes.content}>
        <div className={classes.header}>
          <Typography
            className={classes.heading}
            component="h2"
            id="store-action-heading"
            variant="h6"
          >
            {getStoreActionMessage('heading')}
          </Typography>
          <div className={classes.headerControls}>
            {page ? (
              <FormControl className={classes.filter}>
                <Select
                  inputProps={{
                    id: 'store-action-category-filter',
                    'aria-label': getStoreActionMessage('categoryFilter'),
                  }}
                  native
                  onChange={(event) =>
                    setCategory(
                      event.target.value as CompactStoreActionCategory
                    )
                  }
                  value={selectedCategory}
                >
                  {categories.map((optionCategory) => (
                    <option key={optionCategory} value={optionCategory}>
                      {`${getStoreActionMessage(
                        CATEGORY_MESSAGE_KEYS[optionCategory]
                      )} (${formatStoreActionCount(
                        counts ? counts[optionCategory] : 0
                      )})`}
                    </option>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {page ? (
              <Button
                aria-label={getStoreActionMessage('expand')}
                className={classes.expand}
                onClick={() => setIsExpanded(true)}
                title={getStoreActionMessage('expand')}
                type="button"
              >
                <ExpandIcon aria-hidden="true" />
              </Button>
            ) : null}
          </div>
        </div>
        <StoreActionCompactResults
          role={role}
          classes={classes}
          onDssActivate={onDssActivate}
          onTransferActivate={activateTransfer}
          page={page}
          priceTagQuery={priceTagQuery}
          query={query}
          storeNumber={storeNumber}
          visibleItems={visibleItems}
        />
        <StoreActionExpandedDialog
          isOpen={isExpanded}
          onClose={() => setIsExpanded(false)}
          onDssActivate={onDssActivate}
          onTransferActivate={activateTransfer}
          role={role}
          storeNumber={storeNumber}
        />
        <StoreActionTransferDialog
          activation={transferActivation}
          onClose={closeTransfer}
          storeNumber={storeNumber}
        />
      </CardContent>
    </Card>
  );
};
